import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { useChatScroll } from '@/hooks/use-chat-scroll';
import {
  useBidirectionalChat,
  type ChatMessage,
  type TypingUser,
} from '@/hooks/use-bidirectional-chat';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Send, Paperclip, X, FileText, Image as ImageIcon, Music, Film, Upload, Images, Crown, MapPin, Loader2, Pencil, Check, Users, Info, Search, ChevronUp, ChevronDown, Pin } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { supabase } from '@/integrations/supabase/client';
import { api } from '@/api/api-client';
import { messagingApi } from '@/api/messaging';
import { MessageStatus } from '@/components/messaging/MessageStatus';
import { MessageAttachmentPreview } from '@/components/messaging/MessageAttachmentPreview';
import { MediaCarousel } from '@/components/messaging/MediaCarousel';
import { VoiceRecorder } from '@/components/messaging/VoiceRecorder';
import { MessageActions, MessageReactions } from '@/components/messaging/MessageActions';
import { GroupInfoSheet } from '@/components/messaging/GroupInfoSheet';
import { ProfilePreviewPopover } from '@/components/messaging/ProfilePreviewPopover';
import { useToast } from '@/hooks/use-toast';
import { useMessageSearch } from '@/hooks/use-message-search';
import { Progress } from '@/components/ui/progress';

interface FilePreview {
  file: File;
  preview?: string;
  type: 'image' | 'pdf' | 'audio' | 'video' | 'document';
  uploading?: boolean;
  progress?: number;
  error?: string;
}

const getFileType = (mimeType: string): FilePreview['type'] => {
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('audio/')) return 'audio';
  if (mimeType.startsWith('video/')) return 'video';
  if (mimeType === 'application/pdf') return 'pdf';
  return 'document';
};

// Helper to convert URLs in text to clickable links
// Uses context-aware colors for better contrast on different bubble backgrounds
const linkifyText = (text: string, isOwnMessage: boolean = false): React.ReactNode => {
  // URL regex that matches http, https, and www URLs
  const urlRegex = /(https?:\/\/[^\s<]+[^<.,:;"')\]\s]|www\.[^\s<]+[^<.,:;"')\]\s])/gi;

  const parts = text.split(urlRegex);
  const matches = text.match(urlRegex) || [];

  if (matches.length === 0) return text;

  const result: React.ReactNode[] = [];

  parts.forEach((part, index) => {
    if (part) {
      // Check if this part is a URL
      if (matches.includes(part)) {
        const href = part.startsWith('www.') ? `https://${part}` : part;
        // Own messages have teal-ish background, so use a contrasting blue
        // Other messages have muted bg, standard blue works well
        const linkClass = isOwnMessage
          ? "text-sky-200 hover:text-sky-100 dark:text-sky-300 dark:hover:text-sky-200 underline underline-offset-2 break-all"
          : "text-blue-500 hover:text-blue-400 dark:text-blue-400 dark:hover:text-blue-300 underline underline-offset-2 break-all";
        result.push(
          <a
            key={`link-${index}`}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className={linkClass}
            onClick={(e) => e.stopPropagation()}
          >
            {part}
          </a>
        );
      } else {
        result.push(part);
      }
    }
  });

  return result;
};

const getFileIcon = (type: FilePreview['type']) => {
  switch (type) {
    case 'image':
      return <ImageIcon className="h-4 w-4" />;
    case 'audio':
      return <Music className="h-4 w-4" />;
    case 'video':
      return <Film className="h-4 w-4" />;
    case 'pdf':
    case 'document':
      return <FileText className="h-4 w-4" />;
  }
};

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

// Simple chat message component - memoized to prevent unnecessary re-renders
const ChatMessageItem = React.memo(
  ({
    message,
    isOwnMessage,
    showHeader,
    onAttachmentLoad,
    onMediaClick,
    currentEntityId,
    onEdit,
    onDelete,
    onPin,
    onReact,
    isEditing,
    editContent,
    onEditContentChange,
    onSaveEdit,
    onCancelEdit,
  }: {
    message: ChatMessage;
    isOwnMessage: boolean;
    showHeader: boolean;
    onAttachmentLoad?: () => void;
    onMediaClick?: (mediaId: string) => void;
    currentEntityId?: string;
    onEdit?: (messageId: string) => void;
    onDelete?: (messageId: string) => Promise<void>;
    onPin?: (messageId: string, isPinned: boolean) => Promise<void>;
    onReact?: (messageId: string, emoji: string) => Promise<void>;
    isEditing?: boolean;
    editContent?: string;
    onEditContentChange?: (content: string) => void;
    onSaveEdit?: () => void;
    onCancelEdit?: () => void;
  }) => {
    // Don't render deleted messages
    if (message.deletedAt) {
      return (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className={cn(
            "flex w-full mb-2",
            isOwnMessage ? "justify-end" : "justify-start"
          )}
        >
          <div className="px-4 py-2 text-xs text-muted-foreground italic">
            This message was deleted
          </div>
        </motion.div>
      );
    }

    return (
      <motion.div
        initial={{ opacity: 0, y: 10, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.2 }}
        className={cn(
          "flex w-full mb-2 group", // Added group for hover actions
          isOwnMessage ? "justify-end" : "justify-start"
        )}
      >
        {/* Actions on left side for others' messages */}
        {!isOwnMessage && currentEntityId && (
          <div className="flex items-center mr-1 self-center">
            <MessageActions
              message={message}
              isOwnMessage={false}
              currentEntityId={currentEntityId}
              onEdit={onEdit}
              onDelete={onDelete}
              onPin={onPin}
              onReact={onReact ? (id, emoji) => onReact(id, emoji) : undefined}
            />
          </div>
        )}

        <div
          className={cn(
            "flex flex-col max-w-[85%] md:max-w-[75%]",
            isOwnMessage ? "items-end" : "items-start"
          )}
        >
          {/* Pinned indicator */}
          {message.isPinned && (
            <div className="text-[10px] text-muted-foreground mb-0.5 flex items-center gap-1">
              <span>📌</span> Pinned
            </div>
          )}

          {/* Message Bubble */}
          <div
            className={cn(
              "relative px-4 py-2 shadow-sm",
              isOwnMessage
                ? "bg-primary/20 dark:bg-primary/15 text-foreground border border-primary/30 dark:border-primary/25 rounded-2xl rounded-tr-sm"
                : "bg-muted/60 dark:bg-zinc-800 text-foreground rounded-2xl rounded-tl-sm border border-transparent dark:border-zinc-700",
              (message.hasAttachments || message.attachmentCount > 0) ? "p-2" : ""
            )}
          >
            <div className="flex flex-col gap-1">
              {/* Sender name for group chats */}
              {showHeader && !isOwnMessage && message.user?.name && (
                <div className="text-xs font-semibold text-primary mb-0.5">
                  {message.user.name}
                </div>
              )}

              {/* Attachments */}
              {(message.hasAttachments || message.attachmentCount > 0 || (message.attachments && message.attachments.length > 0)) && (
                <div className="mb-1">
                  <MessageAttachmentPreview
                    messageId={message.id}
                    attachments={message.attachments}
                    isOwnMessage={isOwnMessage}
                    className="rounded-lg overflow-hidden"
                    onLoad={onAttachmentLoad}
                    onMediaClick={onMediaClick}
                  />
                </div>
              )}

              {/* Text Content - Show edit input if editing */}
              {isEditing ? (
                <div className="flex flex-col gap-2">
                  <Input
                    value={editContent || ''}
                    onChange={(e) => onEditContentChange?.(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        onSaveEdit?.();
                      } else if (e.key === 'Escape') {
                        onCancelEdit?.();
                      }
                    }}
                    autoFocus
                    className="min-w-[200px] bg-background/50 border-primary/30"
                  />
                  <div className="flex gap-1 justify-end">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 rounded-full"
                      onClick={onCancelEdit}
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 rounded-full text-primary"
                      onClick={onSaveEdit}
                    >
                      <Check className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ) : (
                message.content && message.content !== '📍 Shared location' && (
                  <div className={cn(
                    "text-sm leading-relaxed whitespace-pre-wrap break-words",
                    (message.hasAttachments || message.attachmentCount > 0) && "mt-1 px-1"
                  )}>
                    {linkifyText(message.content, isOwnMessage)}
                  </div>
                )
              )}
            </div>

            {/* Timestamp and edit indicator */}
             <div
              className={cn(
                "text-[10px] mt-1 flex items-center gap-1 select-none",
                isOwnMessage ? "justify-end text-muted-foreground" : "justify-start text-muted-foreground"
              )}
            >
              {/* Edited indicator */}
              {message.editedAt && (
                <span className="flex items-center gap-0.5">
                  <Pencil className="h-2.5 w-2.5" />
                  edited
                </span>
              )}
              {new Date(message.createdAt).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
              })}
              {isOwnMessage && message.status && (
                <MessageStatus
                  status={message.status}
                  className={cn(
                    "h-3 w-3",
                    message.status === 'read' ? "text-primary" : "text-muted-foreground"
                  )}
                />
              )}
            </div>
          </div>

          {/* Reactions */}
          {message.reactions && message.reactions.length > 0 && currentEntityId && (
            <MessageReactions
              reactions={message.reactions}
              currentEntityId={currentEntityId}
              onReact={onReact ? (emoji) => onReact(message.id, emoji) : undefined}
              isOwnMessage={isOwnMessage}
            />
          )}
        </div>

        {/* Actions on right side for own messages */}
        {isOwnMessage && currentEntityId && (
          <div className="flex items-center ml-1 self-center">
            <MessageActions
              message={message}
              isOwnMessage={true}
              currentEntityId={currentEntityId}
              onEdit={onEdit}
              onDelete={onDelete}
              onPin={onPin}
              onReact={onReact ? (id, emoji) => onReact(id, emoji) : undefined}
            />
          </div>
        )}
      </motion.div>
    );
  }
);

ChatMessageItem.displayName = 'ChatMessageItem';

interface UserInfo {
  id: string;
  name: string;
  role: 'client' | 'filmmaker';
}

interface RecipientInfo {
  id: string;
  role: 'client' | 'filmmaker';
  name?: string;
  avatar?: string;
}

interface BidirectionalChatProps {
  roomName: string;
  username: string;
  userInfo: UserInfo;
  recipientInfo: RecipientInfo | null;
  onMessage?: (messages: ChatMessage[]) => void;
  messages?: ChatMessage[];
  hideHeader?: boolean;
  onMediaClick?: (mediaId: string, messageId?: string) => void;
  onViewProfile?: (id: string, role: 'client' | 'filmmaker') => void;
  onLeaveGroup?: (roomName: string) => void;
}

// Helper function to format date for chat dividers
function formatChatDate(dateString: string): string {
  const date = new Date(dateString);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  if (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  ) {
    return 'Today';
  }
  if (
    date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear()
  ) {
    return 'Yesterday';
  }
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Bidirectional Chat component
 * This is an enhanced version that ensures messages show up for both parties
 * Uses client_id and filmmaker_id instead of user IDs
 */
export const BidirectionalChat = ({
  roomName,
  username,
  userInfo,
  recipientInfo,
  onMessage,
  messages: initialMessages = [],
  hideHeader = false,
  onMediaClick: externalOnMediaClick,
  onViewProfile,
  onLeaveGroup,
}: BidirectionalChatProps) => {
  // Debug logging for chat initialization
  console.log('[BidirectionalChat] Component rendered with props:', {
    roomName,
    username,
    userInfo,
    recipientInfo,
    hasInitialMessages: initialMessages.length > 0,
    initialMessagesCount: initialMessages.length,
  });

  const inputRef = useRef<HTMLTextAreaElement>(null);
  const prevMessagesLengthRef = useRef(0);
  const [recipientDetails, setRecipientDetails] = useState<{
    name: string;
    avatar?: string;
  }>({
    name: recipientInfo?.name || 'Chat',
    avatar: recipientInfo?.avatar,
  });
  const [currentUserTier, setCurrentUserTier] = useState<'free' | 'pro'>('free');

  // Group chat state
  const isGroupChat = roomName.startsWith('group_');
  const [groupInfoOpen, setGroupInfoOpen] = useState(false);
  const [showPinnedMessages, setShowPinnedMessages] = useState(true);
  const [groupDetails, setGroupDetails] = useState<{
    name: string;
    avatar_url?: string;
    memberCount: number;
  } | null>(null);

  // Fetch group details for group chats
  useEffect(() => {
    if (isGroupChat && roomName) {
      messagingApi.getRoomDetails(roomName)
        .then((details) => {
          setGroupDetails({
            name: details.name,
            avatar_url: details.avatar_url,
            memberCount: details.participants.filter(p => p.is_active).length,
          });
        })
        .catch((err) => console.error('Error fetching group details:', err));
    }
  }, [isGroupChat, roomName]);

  // Admin client ID for detecting support chats
  const ADMIN_CLIENT_ID = '7c5377d6-727d-4c6e-bbcb-4b4c34009098';

  // Fetch current user's subscription tier - check both filmmaker and client roles
  useEffect(() => {
    const fetchCurrentUserTier = async () => {
      if (!userInfo?.id) return;

      try {
        // Get current user's user_id from Supabase auth
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        // Check subscription tier from users table directly
        const { data: userData } = await supabase
          .from('users')
          .select('subscription_tier')
          .eq('user_id', user.id)
          .single();

        setCurrentUserTier(userData?.subscription_tier || 'free');
      } catch (error) {
        console.log('Could not fetch current user subscription tier:', error);
      }
    };

    fetchCurrentUserTier();
  }, [userInfo?.id]);

  // Update recipient details when recipientInfo changes (from chat list selection)
  useEffect(() => {
    if (recipientInfo?.name && recipientInfo.name !== 'Unknown') {
      setRecipientDetails(prev => ({
        ...prev,
        name: recipientInfo.name,
        avatar: recipientInfo.avatar || prev.avatar,
      }));
    }
  }, [recipientInfo?.name, recipientInfo?.avatar]);

  // Fetch recipient details to get the most accurate/fresh name
  useEffect(() => {
    const fetchRecipientDetails = async () => {
      if (!recipientInfo?.id || !recipientInfo?.role) return;

      try {
        if (recipientInfo.role === 'filmmaker') {
          const data = await api<any>(`/filmmakers/${recipientInfo.id}`);

          if (data) {
            // Use user object from API which has privacy-respecting names
            // (when show_username_only is true, user.first_name = username, user.last_name = '')
            const user = data.user || {};
            const displayName = user.first_name
              ? `${user.first_name} ${user.last_name || ''}`.trim()
              : recipientInfo.name || `Filmmaker ${recipientInfo.id.substring(0, 8)}`;
            setRecipientDetails({
              name: displayName,
              avatar: data.profile_pic || recipientInfo.avatar,
            });
          }
        } else {
          const data = await api<any>(`/clients/${recipientInfo.id}`);

          if (data) {
            setRecipientDetails({
              name: data.company_name || recipientInfo.name || `Client ${recipientInfo.id.substring(0, 8)}`,
              avatar: data.logo_url || recipientInfo.avatar,
            });
          }
        }
      } catch (error) {
        console.error('Error fetching recipient details:', error);
        // Fallback to recipientInfo name if fetch fails
        if (recipientInfo?.name && recipientInfo.name !== 'Unknown') {
          setRecipientDetails({
            name: recipientInfo.name,
            avatar: recipientInfo.avatar,
          });
        }
      }
    };

    fetchRecipientDetails();
  }, [recipientInfo?.id, recipientInfo?.role, recipientInfo?.name, recipientInfo?.avatar]);

  const {
    messages: realtimeMessages,
    sendMessage,
    isConnected,
    isReconnecting,
    loadMoreMessages,
    hasMoreMessages,
    isLoadingMore,
    typingUsers,
    setTyping,
    updateMessage,
    refreshMessages,
  } = useBidirectionalChat({
    roomName,
    username,
    entityId: userInfo.id,
    entityRole: userInfo.role,
  });

  // Debug logging for connection state
  console.log('[BidirectionalChat] Hook returned state:', {
    isConnected,
    isReconnecting,
    realtimeMessagesCount: realtimeMessages.length,
    hasMoreMessages,
    isLoadingMore,
    typingUsersCount: typingUsers.length,
    roomName,
    entityId: userInfo.id,
    entityRole: userInfo.role,
  });

  // Track connection state changes explicitly
  useEffect(() => {
    console.log('[BidirectionalChat] 🔌 CONNECTION CHANGED:', {
      isConnected,
      isReconnecting,
      roomName,
      timestamp: new Date().toISOString()
    });

    // Alert if connection fails to establish after messages load
    if (!isConnected && realtimeMessages.length > 0) {
      console.warn('[BidirectionalChat] ⚠️ MESSAGES LOADED BUT NOT CONNECTED - input will be blocked');
    }
  }, [isConnected, isReconnecting]);

  // Track last typing time for debouncing
  const lastTypingRef = useRef<number>(0);

  const [newMessage, setNewMessage] = useState('');
  const [filePreviews, setFilePreviews] = useState<FilePreview[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  // Track when input would be disabled (must be after uploadingFiles is declared)
  const inputDisabled = !isConnected || uploadingFiles;
  console.log('[BidirectionalChat] INPUT STATE:', {
    disabled: inputDisabled,
    reason: !isConnected ? 'NOT_CONNECTED' : (uploadingFiles ? 'UPLOADING_FILES' : 'ENABLED'),
    isConnected,
    uploadingFiles,
  });
  const [pendingMessageId, setPendingMessageId] = useState<string | null>(null);
  const [pendingAttachments, setPendingAttachments] = useState<Record<string, any[]>>({});
  const [carouselMedia, setCarouselMedia] = useState<{
    isOpen: boolean;
    mediaId: string;
    messageId?: string;
  }>({
    isOpen: false,
    mediaId: '',
    messageId: undefined
  });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragCounter = useRef(0);
  const [sharingLocation, setSharingLocation] = useState(false);
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const { toast } = useToast();

  // In-chat search state
  const [searchOpen, setSearchOpen] = useState(false);
  const [currentSearchIndex, setCurrentSearchIndex] = useState(0);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // In-chat message search hook
  const {
    query: searchQuery,
    setQuery: setSearchQuery,
    results: searchResults,
    isSearching,
    hasResults: hasSearchResults,
  } = useMessageSearch({
    entityId: userInfo.id,
    entityRole: userInfo.role,
    roomName: roomName,
    debounceMs: 150,
    enabled: searchOpen,
  });

  // Navigate between search results
  const handleSearchNavigate = useCallback((direction: 'up' | 'down') => {
    if (!hasSearchResults) return;
    setCurrentSearchIndex(prev => {
      if (direction === 'down') {
        return prev < searchResults.length - 1 ? prev + 1 : 0;
      } else {
        return prev > 0 ? prev - 1 : searchResults.length - 1;
      }
    });
  }, [hasSearchResults, searchResults.length]);

  // Scroll to current search result
  useEffect(() => {
    if (hasSearchResults && searchResults[currentSearchIndex]) {
      const messageId = searchResults[currentSearchIndex].message_id;
      const messageElement = document.getElementById(`message-${messageId}`);
      if (messageElement) {
        messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Highlight the message briefly
        messageElement.classList.add('ring-2', 'ring-primary', 'rounded-xl');
        setTimeout(() => {
          messageElement.classList.remove('ring-2', 'ring-primary', 'rounded-xl');
        }, 2000);
      }
    }
  }, [currentSearchIndex, hasSearchResults, searchResults]);

  // Focus search input when opened
  useEffect(() => {
    if (searchOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [searchOpen]);

  // Close search with Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && searchOpen) {
        setSearchOpen(false);
        setSearchQuery('');
        setCurrentSearchIndex(0);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [searchOpen, setSearchQuery]);

  // Message action handlers
  const handleEditMessage = useCallback((messageId: string) => {
    const message = realtimeMessages.find(m => m.id === messageId);
    if (message) {
      setEditingMessageId(messageId);
      setEditContent(message.content);
    }
  }, [realtimeMessages]);

  const handleSaveEdit = useCallback(async () => {
    if (!editingMessageId || !editContent.trim()) return;

    try {
      const response = await api<{ success: boolean; data: any }>(`/messages/${editingMessageId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          content: editContent.trim(),
          entity_id: userInfo.id,
        }),
      });

      if (response.success) {
        // Update local state with edited content
        updateMessage(editingMessageId, {
          content: editContent.trim(),
          editedAt: new Date().toISOString(),
        });
        toast({ title: 'Message edited' });
        setEditingMessageId(null);
        setEditContent('');
      }
    } catch (error: any) {
      toast({
        title: 'Failed to edit message',
        description: error.message || 'Please try again',
        variant: 'destructive',
      });
    }
  }, [editingMessageId, editContent, userInfo.id, toast, updateMessage]);

  const handleCancelEdit = useCallback(() => {
    setEditingMessageId(null);
    setEditContent('');
  }, []);

  const handleDeleteMessage = useCallback(async (messageId: string) => {
    try {
      const response = await api<{ success: boolean }>(`/messages/${messageId}?entity_id=${userInfo.id}`, {
        method: 'DELETE',
      });

      if (response.success) {
        // Update local state - mark as deleted
        updateMessage(messageId, {
          deletedAt: new Date().toISOString(),
          content: '',
        });
        toast({ title: 'Message deleted' });
      }
    } catch (error: any) {
      toast({
        title: 'Failed to delete message',
        description: error.message || 'Cannot delete read messages',
        variant: 'destructive',
      });
      throw error;
    }
  }, [userInfo.id, toast, updateMessage]);

  const handlePinMessage = useCallback(async (messageId: string, isCurrentlyPinned: boolean) => {
    try {
      if (isCurrentlyPinned) {
        await api<{ success: boolean }>(`/messages/${messageId}/pin?room_name=${encodeURIComponent(roomName)}`, {
          method: 'DELETE',
        });
        // Update local state
        updateMessage(messageId, { isPinned: false });
        toast({ title: 'Message unpinned' });
      } else {
        await api<{ success: boolean }>(`/messages/${messageId}/pin`, {
          method: 'POST',
          body: JSON.stringify({
            room_name: roomName,
            entity_id: userInfo.id,
            entity_role: userInfo.role,
          }),
        });
        // Update local state
        updateMessage(messageId, { isPinned: true });
        toast({ title: 'Message pinned' });
      }
    } catch (error: any) {
      toast({
        title: `Failed to ${isCurrentlyPinned ? 'unpin' : 'pin'} message`,
        description: error.message,
        variant: 'destructive',
      });
      throw error;
    }
  }, [roomName, userInfo.id, userInfo.role, toast, updateMessage]);

  const handleReactToMessage = useCallback(async (messageId: string, emoji: string) => {
    try {
      // Check if user already reacted with this emoji
      const message = realtimeMessages.find(m => m.id === messageId);
      const existingReaction = message?.reactions?.find(
        r => r.entityId === userInfo.id && r.emoji === emoji
      );

      if (existingReaction) {
        // Remove reaction
        await api<{ success: boolean }>(`/messages/${messageId}/reactions?entity_id=${userInfo.id}`, {
          method: 'DELETE',
        });
        // Update local state - remove this user's reaction
        const updatedReactions = (message?.reactions || []).filter(
          r => !(r.entityId === userInfo.id && r.emoji === emoji)
        );
        updateMessage(messageId, { reactions: updatedReactions });
      } else {
        // Add reaction
        const response = await api<{ success: boolean; data: any }>(`/messages/${messageId}/reactions`, {
          method: 'POST',
          body: JSON.stringify({
            emoji,
            entity_id: userInfo.id,
            entity_role: userInfo.role,
          }),
        });
        // Update local state - add the new reaction
        const newReaction = {
          id: response.data?.id || crypto.randomUUID(),
          emoji,
          entityId: userInfo.id,
          entityRole: userInfo.role,
          createdAt: new Date().toISOString(),
        };
        const updatedReactions = [...(message?.reactions || []), newReaction];
        updateMessage(messageId, { reactions: updatedReactions });
      }
    } catch (error: any) {
      toast({
        title: 'Failed to react',
        description: error.message,
        variant: 'destructive',
      });
      throw error;
    }
  }, [realtimeMessages, userInfo.id, userInfo.role, toast, updateMessage]);

  // Auto-focus the input field when component mounts or roomName changes
  useEffect(() => {
    if (isConnected && inputRef.current) {
      // Short delay to ensure the component is fully rendered
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [roomName, isConnected]);

  // Function to check if a message belongs to the current user
  const checkIfOwnMessage = useCallback(
    (
      messageEntityId?: string,
      messageEntityRole?: string,
      messageUserId?: string
    ) => {
      // First check if we have the entity info from the message
      if (messageEntityId && messageEntityRole) {
        return (
          messageEntityId === userInfo.id && messageEntityRole === userInfo.role
        );
      }

      // Fallback to checking user ID if entity info is missing
      return messageUserId === userInfo.id;
    },
    [userInfo]
  );

  // Merge realtime messages with initial messages - memoized to prevent unnecessary recalculations
  const allMessages = useMemo(() => {
    // Skip merging if no new messages
    if (realtimeMessages.length === 0 && initialMessages.length === 0) {
      return [];
    }

    // Put realtimeMessages first so they take precedence (they have reactions, isPinned, etc.)
    const mergedMessages = [...realtimeMessages, ...initialMessages];

    // Remove duplicates based on message id and filter out invalid messages
    const uniqueMessages = mergedMessages.filter(
      (message, index, self) =>
        message && message.id && message.createdAt &&
        index === self.findIndex((m) => m.id === message.id)
    );

    // Sort by creation date (with null safety)
    const sortedMessages = uniqueMessages.sort((a, b) => {
      const dateA = a.createdAt || '';
      const dateB = b.createdAt || '';
      return dateA.localeCompare(dateB);
    });

    // Add pending attachments to messages
    const messagesWithAttachments = sortedMessages.map(message => {
      if (pendingAttachments[message.id]) {
        return {
          ...message,
          attachments: pendingAttachments[message.id],
          hasAttachments: true,
          attachmentCount: pendingAttachments[message.id].length
        };
      }
      return message;
    });

    return messagesWithAttachments;
  }, [initialMessages, realtimeMessages, pendingAttachments]);

  // Get pinned messages for display at the top
  const pinnedMessages = useMemo(() => {
    return allMessages.filter(m => m.isPinned);
  }, [allMessages]);

  // Use enhanced useChatScroll hook with allMessages and roomName
  const { containerRef, scrollToBottom, instantScrollToBottom } = useChatScroll(
    allMessages,
    [roomName],
    roomName
  );

  // Add scroll event listener for infinite scroll
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      // Check if user has scrolled to the top (for loading older messages)
      // In a flex-col-reverse container, scrollTop = 0 means we're at the bottom
      const scrollFromBottom = container.scrollTop;
      const scrollHeight = container.scrollHeight;
      const clientHeight = container.clientHeight;

      // Calculate distance from top (in flex-col-reverse, this is inverted)
      const distanceFromTop = scrollHeight - clientHeight - scrollFromBottom;

      // Load more when within 100px of the top
      if (distanceFromTop < 100 && hasMoreMessages && !isLoadingMore) {
        // Store current scroll position before loading
        const previousScrollHeight = container.scrollHeight;

        // Load more messages
        loadMoreMessages();

        // Maintain scroll position after new messages are loaded
        requestAnimationFrame(() => {
          setTimeout(() => {
            const newScrollHeight = container.scrollHeight;
            const heightDiff = newScrollHeight - previousScrollHeight;
            if (heightDiff > 0) {
              // Adjust scroll to maintain visual position
              container.scrollTop = scrollFromBottom;
            }
          }, 50);
        });
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [containerRef, hasMoreMessages, isLoadingMore, loadMoreMessages]);

  // Clean up old pending attachments periodically
  useEffect(() => {
    const cleanup = setInterval(() => {
      setPendingAttachments(prev => {
        const filtered: Record<string, any[]> = {};
        Object.keys(prev).forEach(messageId => {
          // Keep attachments for messages that still exist
          const messageExists = allMessages.some(m => m.id === messageId);
          if (messageExists) {
            filtered[messageId] = prev[messageId];
          }
        });
        return filtered;
      });
    }, 60000); // Run every minute

    return () => clearInterval(cleanup);
  }, [allMessages]);

  // No need for initial scroll - column-reverse handles it naturally

  // Only call onMessage when messages actually change
  useEffect(() => {
    if (onMessage && allMessages.length !== prevMessagesLengthRef.current) {
      prevMessagesLengthRef.current = allMessages.length;
      onMessage(allMessages);
    }
  }, [allMessages, onMessage]);

  // Upload attachments when sending
  const uploadAttachments = useCallback(async () => {
    if (filePreviews.length === 0) return;

    setUploadingFiles(true);

    // Generate message ID that will be used for the message
    const messageId = crypto.randomUUID();

    // Use message text if provided, otherwise empty for media-only messages
    const messageText = newMessage.trim();

    try {
      // IMPORTANT: Create the message FIRST to satisfy foreign key constraint
      // Send message with attachment metadata
      const attachmentInfo = {
        hasAttachments: true,
        attachmentCount: filePreviews.length,
        messageType: filePreviews.some(fp => fp.type === 'image') ? 'media' : 'mixed',
        messageId: messageId,
      };

      // Create the message in the database BEFORE uploading attachments
      await sendMessage(messageText, attachmentInfo);

      // Now upload attachments - the message exists so foreign key will be satisfied
      const uploadedAttachments = [];

      // Upload attachments after message creation
      const uploadPromises = filePreviews.map(async (filePreview, index) => {
        // Update progress
        setFilePreviews(prev => {
          const updated = [...prev];
          updated[index] = { ...updated[index], uploading: true, progress: 0 };
          return updated;
        });

        // Convert to base64
        const reader = new FileReader();
        const base64Promise = new Promise<string>((resolve, reject) => {
          reader.onload = () => {
            const base64 = (reader.result as string).split(',')[1];
            resolve(base64);
          };
          reader.onerror = reject;
        });
        reader.readAsDataURL(filePreview.file);

        const base64Data = await base64Promise;

        // Update progress
        setFilePreviews(prev => {
          const updated = [...prev];
          if (updated[index]) {
            updated[index] = { ...updated[index], progress: 50 };
          }
          return updated;
        });

        // Prepare attachment data
        const attachmentData = {
          message_id: messageId, // This message doesn't exist yet, but we'll create it with this ID
          room_name: roomName,
          file_name: filePreview.file.name,
          file_type: filePreview.type === 'document' && filePreview.file.type === 'application/pdf' ? 'pdf' : filePreview.type,
          mime_type: filePreview.file.type,
          file_size: filePreview.file.size,
          file_data: base64Data,
          entity_id: userInfo.id, // Include entity_id for consistent storage paths
        };

        // Upload to edge function
        const { data: { session } } = await supabase.auth.getSession();
        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/message-attachments`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${session?.access_token}`,
            },
            body: JSON.stringify(attachmentData),
          }
        );

        if (!response.ok) {
          const error = await response.json();
          console.error(`❌ Upload failed for file ${index + 1}:`, error);
          throw new Error(error.error || 'Upload failed');
        }

        const uploadResult = await response.json();

        // Update progress
        setFilePreviews(prev => {
          const updated = [...prev];
          if (updated[index]) {
            updated[index] = { ...updated[index], progress: 100, uploading: false };
          }
          return updated;
        });

        return uploadResult;
      });

      // Wait for all uploads to complete FIRST
      const uploadResults = await Promise.all(uploadPromises);

      // Store attachment data locally for immediate display
      const attachmentData = uploadResults.map((result, index) => {
        if (result && filePreviews[index]) {
          const filePreview = filePreviews[index];
          // Use the actual attachment data from the upload result
          // Always use our local type detection to ensure consistency
          // The server might return different format for file_type
          let correctFileType = filePreview.type;

          // Special case for PDFs
          if (filePreview.type === 'document' && filePreview.file.type === 'application/pdf') {
            correctFileType = 'pdf';
          }

          return {
            attachment_id: result.attachment_id || crypto.randomUUID(),
            file_url: result.file_url || '',
            file_name: result.file_name || filePreview.file.name,
            file_type: correctFileType,
            mime_type: result.mime_type || filePreview.file.type,
            file_size: result.file_size || filePreview.file.size,
            signed_url: result.signed_url || '',
            thumbnail_url: result.thumbnail_url,
            thumbnail_signed_url: result.thumbnail_signed_url,
          };
        }
        return null;
      }).filter(Boolean);

      // Store attachments for this message for display
      setPendingAttachments(prev => ({
        ...prev,
        [messageId]: attachmentData
      }));

      // Verify attachments were saved
      const { data: { session } } = await supabase.auth.getSession();
      const verifyResponse = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/message-attachments?message_id=${messageId}`,
        {
          headers: {
            Authorization: `Bearer ${session?.access_token}`,
          },
        }
      );

      if (verifyResponse.ok) {
        const attachments = await verifyResponse.json();

        // Update pending attachments with signed URLs from the server
        // But preserve our local file_type detection
        if (attachments.length > 0) {
          const correctedAttachments = attachments.map((att: any, index: number) => {
            // Use our local file type if available
            const localFileType = attachmentData[index]?.file_type;
            return {
              ...att,
              file_type: localFileType || att.file_type
            };
          });

          setPendingAttachments(prev => ({
            ...prev,
            [messageId]: correctedAttachments
          }));
        }
      } else {
        console.error('❌ Failed to verify attachments');
      }

      // Clear everything
      setNewMessage('');
      setFilePreviews([]);
      setPendingMessageId(messageId);

    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: 'Upload failed',
        description: error instanceof Error ? error.message : 'Failed to upload files',
        variant: 'destructive',
      });

      // Mark files as failed
      setFilePreviews(prev => prev.map(fp => ({ ...fp, uploading: false, error: 'Upload failed' })));
    } finally {
      setUploadingFiles(false);
    }
  }, [filePreviews, newMessage, sendMessage, toast, roomName, userInfo]);

  // Handle sending a new message with attachments - stable callback
  const handleSendMessage = useCallback(
    async (e?: React.FormEvent) => {
      if (e) e.preventDefault();

      // Check if we have text or attachments to send
      if (!newMessage.trim() && filePreviews.length === 0) return;
      if (!isConnected) return;

      // If we have attachments, upload them first
      if (filePreviews.length > 0) {
        await uploadAttachments();
      } else {
        // Just send the text message
        sendMessage(newMessage);
        setNewMessage('');
      }

      // Clear typing status after sending
      setTyping(false);
    },
    [newMessage, isConnected, sendMessage, filePreviews, uploadAttachments, setTyping]
  );

  // Handle file attachment upload
  const handleAttachmentUpload = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // Handle file selection - just add to previews, don't upload yet
  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const newPreviews: FilePreview[] = [];

    for (const file of Array.from(files)) {
      // Validate file size (50MB limit)
      if (file.size > 52428800) {
        toast({
          title: 'File too large',
          description: `${file.name} exceeds the 50MB limit`,
          variant: 'destructive',
        });
        continue;
      }

      const fileType = getFileType(file.type);
      const preview: FilePreview = {
        file,
        type: fileType,
      };

      // Generate preview for images
      if (fileType === 'image') {
        const reader = new FileReader();
        reader.onload = () => {
          preview.preview = reader.result as string;
          setFilePreviews(prev => [...prev]); // Force re-render
        };
        reader.readAsDataURL(file);
      }

      newPreviews.push(preview);
    }

    setFilePreviews(prev => [...prev, ...newPreviews]);

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [toast]);

  // Remove a file from previews
  const handleRemoveFile = useCallback((index: number) => {
    setFilePreviews(prev => prev.filter((_, i) => i !== index));
  }, []);

  // Drag and drop handlers
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounter.current = 0;

    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;

    const newPreviews: FilePreview[] = [];

    for (const file of Array.from(files)) {
      // Validate file size (50MB limit)
      if (file.size > 52428800) {
        toast({
          title: 'File too large',
          description: `${file.name} exceeds the 50MB limit`,
          variant: 'destructive',
        });
        continue;
      }

      const fileType = getFileType(file.type);
      const preview: FilePreview = {
        file,
        type: fileType,
      };

      // Generate preview for images
      if (fileType === 'image') {
        const reader = new FileReader();
        reader.onload = () => {
          preview.preview = reader.result as string;
          setFilePreviews(prev => [...prev]); // Force re-render
        };
        reader.readAsDataURL(file);
      }

      newPreviews.push(preview);
    }

    setFilePreviews(prev => [...prev, ...newPreviews]);
  }, [toast]);

  // Location sharing handler
  const handleLocationShare = useCallback(async () => {
    if (!navigator.geolocation) {
      toast({
        title: 'Location not supported',
        description: 'Your browser does not support geolocation',
        variant: 'destructive',
      });
      return;
    }

    setSharingLocation(true);

    try {
      // Get current position
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000,
        });
      });

      const { latitude, longitude } = position.coords;

      // Create the message first with a specific ID
      const messageId = crypto.randomUUID();

      // Send message to create it in database with our message ID
      await sendMessage('📍 Shared location', {
        hasAttachments: true,
        attachmentCount: 1,
        messageType: 'location',
        messageId: messageId,
      });

      // Call the location-share edge function
      const { data: sessionData } = await supabase.auth.getSession();
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/location-share`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${sessionData?.session?.access_token}`,
          },
          body: JSON.stringify({
            latitude,
            longitude,
            message_id: messageId,
            room_name: roomName,
            entity_id: userInfo.id,
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to share location');
      }

      const locationData = await response.json();

      // Add to pending attachments for immediate display
      if (locationData.attachment) {
        setPendingAttachments(prev => ({
          ...prev,
          [messageId]: [{
            ...locationData.attachment,
            file_type: 'location',
            metadata: locationData.location,
          }]
        }));
      }

      toast({
        title: 'Location shared',
        description: locationData.location?.address || 'Your location has been shared',
      });

      scrollToBottom();
    } catch (error: any) {
      console.error('Location share error:', error);

      let errorMessage = 'Failed to share location';
      if (error.code === 1) {
        errorMessage = 'Location permission denied. Please enable location access.';
      } else if (error.code === 2) {
        errorMessage = 'Unable to determine your location. Please try again.';
      } else if (error.code === 3) {
        errorMessage = 'Location request timed out. Please try again.';
      }

      toast({
        title: 'Location Error',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setSharingLocation(false);
    }
  }, [roomName, userInfo, username, sendMessage, toast, scrollToBottom]);

  return (
    <div
      className="flex flex-col h-full w-full bg-background text-foreground antialiased overflow-hidden relative"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Drag overlay */}
      {isDragging && (
        <div className="absolute inset-0 z-50 bg-background/90 backdrop-blur-sm flex items-center justify-center pointer-events-none">
          <div className="bg-card border-2 border-dashed border-primary rounded-lg p-8 text-center">
            <Upload className="h-12 w-12 mx-auto mb-4 text-primary" />
            <p className="text-lg font-medium">Drop files here to upload</p>
            <p className="text-sm text-muted-foreground mt-1">Images, PDFs, Audio, Video (up to 50MB)</p>
          </div>
        </div>
      )}
      {/* In-chat search bar */}
      {searchOpen && (
        <div className="flex items-center gap-2 p-2 border-b bg-background/95 backdrop-blur z-20">
          <Search className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <Input
            ref={searchInputRef}
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentSearchIndex(0);
            }}
            placeholder="Search in this chat..."
            className="flex-1 h-8 text-sm border-none focus-visible:ring-0 bg-transparent"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSearchNavigate('down');
              }
            }}
          />
          {isSearching && (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          )}
          {hasSearchResults && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <span>{currentSearchIndex + 1} / {searchResults.length}</span>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={() => handleSearchNavigate('up')}
              >
                <ChevronUp className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={() => handleSearchNavigate('down')}
              >
                <ChevronDown className="h-4 w-4" />
              </Button>
            </div>
          )}
          {searchQuery && !isSearching && !hasSearchResults && (
            <span className="text-xs text-muted-foreground">No results</span>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={() => {
              setSearchOpen(false);
              setSearchQuery('');
              setCurrentSearchIndex(0);
            }}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Chat header - only show if not hidden */}
      {!hideHeader && (() => {
        // Check if this is a priority support chat (pro user chatting with admin)
        const isPrioritySupport = currentUserTier === 'pro' && recipientInfo?.id === ADMIN_CLIENT_ID;

        // Group chat header
        if (isGroupChat && groupDetails) {
          return (
            <div className="p-4 flex items-center gap-4 flex-shrink-0 z-10 backdrop-blur-md bg-background/80 supports-[backdrop-filter]:bg-background/60 border-b border-border/40 shadow-sm">
              {/* Clickable group info area */}
              <button
                onClick={() => setGroupInfoOpen(true)}
                className="flex items-center gap-4 flex-1 min-w-0 text-left cursor-pointer hover:opacity-80 transition-opacity"
              >
                <div className="relative">
                  <Avatar className="h-10 w-10 ring-2 ring-background shadow-sm">
                    {groupDetails.avatar_url ? (
                      <AvatarImage src={groupDetails.avatar_url} alt={groupDetails.name} className="object-cover" />
                    ) : (
                      <AvatarFallback className="bg-primary text-primary-foreground">
                        <Users className="h-5 w-5" />
                      </AvatarFallback>
                    )}
                  </Avatar>
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h2 className="font-semibold text-base truncate">{groupDetails.name}</h2>
                    <Badge variant="secondary" className="px-1.5 py-0 h-5 text-[10px]">
                      <Users className="h-3 w-3 mr-1" />
                      {groupDetails.memberCount}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground truncate">
                    Tap for group info
                  </p>
                </div>
              </button>

              {/* Search Button */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSearchOpen(true)}
                className="h-9 w-9 rounded-full hover:bg-muted"
                title="Search in chat"
              >
                <Search className="h-5 w-5 text-muted-foreground" />
              </Button>

              {/* Group Info Button */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setGroupInfoOpen(true)}
                className="h-9 w-9 rounded-full hover:bg-muted"
                title="Group info"
              >
                <Info className="h-5 w-5 text-muted-foreground" />
              </Button>

              {/* Media Gallery Button */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setCarouselMedia({ isOpen: true, mediaId: '', messageId: undefined })}
                className="h-9 w-9 rounded-full hover:bg-muted"
                title="View all media"
              >
                <Images className="h-5 w-5 text-muted-foreground" />
              </Button>
            </div>
          );
        }

        // Direct chat header
        return (
          <div className={cn(
            "p-4 flex items-center gap-4 flex-shrink-0 z-10 backdrop-blur-md bg-background/80 supports-[backdrop-filter]:bg-background/60 border-b border-border/40 shadow-sm",
            isPrioritySupport && "bg-gradient-to-r from-yellow-50/50 to-transparent dark:from-yellow-950/20 dark:to-transparent"
          )}>
            {/* Clickable profile area with popover */}
            {recipientInfo?.id && recipientInfo?.role ? (
              <ProfilePreviewPopover
                entityId={recipientInfo.id}
                entityRole={recipientInfo.role}
                onViewFullProfile={() => onViewProfile?.(recipientInfo.id, recipientInfo.role)}
                side="bottom"
                align="start"
              >
                <button
                  className="flex items-center gap-4 flex-1 min-w-0 text-left cursor-pointer hover:opacity-80 transition-opacity"
                >
                  <div className="relative">
                    <Avatar className={cn(
                      "h-10 w-10 ring-2 ring-background shadow-sm",
                      isPrioritySupport && "ring-yellow-500/50"
                    )}>
                      {recipientDetails.avatar ? (
                        <AvatarImage
                          src={recipientDetails.avatar}
                          alt={recipientDetails.name}
                          className="object-cover"
                        />
                      ) : (
                        <AvatarFallback className={cn(
                          "bg-primary/10 text-primary font-medium",
                          isPrioritySupport && "bg-gradient-to-br from-yellow-400 to-yellow-600 text-white"
                        )}>
                          {recipientDetails.name.slice(0, 2).toUpperCase()}
                        </AvatarFallback>
                      )}
                    </Avatar>
                    {isPrioritySupport && (
                      <div className="absolute -top-1 -right-1 w-5 h-5 bg-yellow-500 rounded-full flex items-center justify-center ring-2 ring-background">
                        <Crown className="h-3 w-3 text-white" />
                      </div>
                    )}
                    <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-background rounded-full" />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h2 className="font-semibold text-base truncate">
                        {recipientDetails.name}
                      </h2>
                      {isPrioritySupport && (
                        <Badge variant="secondary" className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300 border-yellow-200 dark:border-yellow-800/30 px-1.5 py-0 h-5 text-[10px]">
                          PRO SUPPORT
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground truncate">
                      {isPrioritySupport ? "Priority Support Channel" : (recipientInfo?.role === 'filmmaker' ? "Filmmaker" : "Client")}
                    </p>
                  </div>
                </button>
              </ProfilePreviewPopover>
            ) : (
              <div className="flex items-center gap-4 flex-1 min-w-0">
                <Avatar className="h-10 w-10 ring-2 ring-background shadow-sm">
                  <AvatarFallback className="bg-primary/10 text-primary font-medium">
                    {recipientDetails.name.slice(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <h2 className="font-semibold text-base truncate">{recipientDetails.name}</h2>
                  <p className="text-xs text-muted-foreground truncate">Chat</p>
                </div>
              </div>
            )}

            {/* Search Button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSearchOpen(true)}
              className="h-9 w-9 rounded-full hover:bg-muted"
              title="Search in chat"
            >
              <Search className="h-5 w-5 text-muted-foreground" />
            </Button>

            {/* Media Gallery Button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                // Open carousel without initial media (shows all media)
                setCarouselMedia({ isOpen: true, mediaId: '', messageId: undefined });
              }}
              className="h-9 w-9 rounded-full hover:bg-muted"
              title="View all media"
            >
              <Images className="h-5 w-5 text-muted-foreground" />
            </Button>
          </div>
        );
      })()}

      {/* Connection status - only show if no messages are loaded */}
      {!isConnected && allMessages.length === 0 && (
        <div className="bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 text-xs md:text-sm p-2 text-center">
          {isReconnecting ? 'Reconnecting to chat...' : 'Connecting to chat...'}
        </div>
      )}

      {/* Pinned Messages Section */}
      {pinnedMessages.length > 0 && (
        <div className="border-b border-border bg-muted/30">
          <button
            onClick={() => setShowPinnedMessages(!showPinnedMessages)}
            className="w-full px-3 py-2 flex items-center justify-between hover:bg-muted/50 transition-colors"
          >
            <div className="flex items-center gap-2 text-sm font-medium">
              <Pin className="h-4 w-4 text-primary" />
              <span>Pinned Messages ({pinnedMessages.length})</span>
            </div>
            {showPinnedMessages ? (
              <ChevronUp className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            )}
          </button>
          {showPinnedMessages && (
            <div className="px-3 pb-3 space-y-2 max-h-48 overflow-y-auto">
              {pinnedMessages.map((message) => (
                <div
                  key={`pinned-${message.id}`}
                  className="bg-background rounded-lg p-2 border border-border/50 cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => {
                    // Scroll to the message in the chat
                    const messageEl = document.getElementById(`message-${message.id}`);
                    if (messageEl) {
                      messageEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      // Add a brief highlight effect
                      messageEl.classList.add('ring-2', 'ring-primary', 'ring-offset-2');
                      setTimeout(() => {
                        messageEl.classList.remove('ring-2', 'ring-primary', 'ring-offset-2');
                      }, 2000);
                    }
                  }}
                >
                  <div className="flex items-start gap-2">
                    <Avatar className="h-6 w-6 flex-shrink-0">
                      <AvatarImage src={message.user.avatar || ''} />
                      <AvatarFallback className="text-xs">
                        {message.user.name?.charAt(0) || '?'}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium truncate">{message.user.name}</span>
                        <span className="text-[10px] text-muted-foreground">
                          {new Date(message.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground line-clamp-2">
                        {message.content || (message.hasAttachments ? '📎 Attachment' : '')}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Messages - using column-reverse to start at bottom naturally */}
      <div
        ref={containerRef}
        className="flex-1 overflow-y-auto p-2 md:p-4 flex flex-col-reverse"
      >
        <div className="flex flex-col gap-1">
          {/* Loading indicator at the top (oldest messages) */}
          {isLoadingMore && (
            <div className="flex justify-center py-2">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-primary"></div>
                <span>Loading older messages...</span>
              </div>
            </div>
          )}

          {/* Show indicator when all messages are loaded */}
          {!hasMoreMessages && allMessages.length > 0 && (
            <div className="text-center text-xs text-muted-foreground py-2">
              Beginning of conversation
            </div>
          )}

          {allMessages.length === 0 ? (
            <div className="text-center text-sm text-muted-foreground py-4">
              No messages yet. Start the conversation!
            </div>
          ) : (
            /* Keep messages in normal order - latest at the end */
            allMessages.map((message, index) => {
            const prevMessage = index > 0 ? allMessages[index - 1] : null;
            const showHeader =
              !prevMessage || prevMessage.user.name !== message.user.name;

            // Date divider logic
            const currentDate = message.createdAt.split('T')[0];
            const prevDate = prevMessage
              ? prevMessage.createdAt.split('T')[0]
              : null;
            const showDateDivider = !prevMessage || currentDate !== prevDate;

            return (
              <React.Fragment key={message.id}>
                {showDateDivider && (
                  <div className="flex justify-center my-2">
                    <span className="text-xs bg-muted px-3 py-1 rounded-full text-muted-foreground">
                      {formatChatDate(message.createdAt)}
                    </span>
                  </div>
                )}
                <div id={`message-${message.id}`} className="animate-in fade-in slide-in-from-bottom-4 duration-300 transition-all">
                  <ChatMessageItem
                    message={message}
                    isOwnMessage={checkIfOwnMessage(
                      message.entityId,
                      message.entityRole,
                      message.user.id
                    )}
                    showHeader={showHeader}
                    onAttachmentLoad={scrollToBottom}
                    onMediaClick={
                      externalOnMediaClick
                        ? (mediaId) => externalOnMediaClick(mediaId, message.id)
                        : (mediaId) => setCarouselMedia({ isOpen: true, mediaId, messageId: message.id })
                    }
                    currentEntityId={userInfo.id}
                    onEdit={handleEditMessage}
                    onDelete={handleDeleteMessage}
                    onPin={handlePinMessage}
                    onReact={handleReactToMessage}
                    isEditing={editingMessageId === message.id}
                    editContent={editingMessageId === message.id ? editContent : undefined}
                    onEditContentChange={setEditContent}
                    onSaveEdit={handleSaveEdit}
                    onCancelEdit={handleCancelEdit}
                  />
                </div>
              </React.Fragment>
            );
          })
        )}
        </div>
      </div>

      {/* Typing indicator - chat pill style */}
      {typingUsers.length > 0 && (
        <div className="px-2 md:px-4 pb-1">
          <div className="flex justify-start">
            <div className="bg-muted rounded-xl px-3 py-2 flex items-center gap-2">
              <div className="flex gap-1">
                <span className="w-1.5 h-1.5 bg-muted-foreground/60 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-1.5 h-1.5 bg-muted-foreground/60 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-1.5 h-1.5 bg-muted-foreground/60 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Message input with file preview */}
      <div className="p-4 flex-shrink-0 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        {/* File previews */}
        {filePreviews.length > 0 && (
          <div className="mb-3 p-3 rounded-xl border border-border/50 bg-muted/30 backdrop-blur-sm">
            <div className="flex flex-wrap gap-3">
              {filePreviews.map((preview, index) => (
                <div
                  key={index}
                  className="relative group bg-card border rounded-lg p-2 flex items-center gap-3 shadow-sm min-w-[180px]"
                >
                  {/* File preview */}
                  {preview.type === 'image' && preview.preview ? (
                    <img
                      src={preview.preview}
                      alt={preview.file.name}
                      className="h-12 w-12 object-cover rounded-md"
                    />
                  ) : (
                    <div className="h-12 w-12 flex items-center justify-center bg-muted rounded-md text-muted-foreground">
                      {getFileIcon(preview.type)}
                    </div>
                  )}

                  {/* File info */}
                  <div className="flex-1 min-w-0 overflow-hidden">
                    <p className="text-xs font-medium truncate" title={preview.file.name}>
                      {preview.file.name}
                    </p>
                    <p className="text-[10px] text-muted-foreground">
                      {formatFileSize(preview.file.size)}
                    </p>
                    {preview.uploading && (
                      <Progress value={preview.progress || 0} className="h-1 mt-1.5" />
                    )}
                    {preview.error && (
                      <p className="text-[10px] text-destructive mt-1">{preview.error}</p>
                    )}
                  </div>

                  {/* Remove button */}
                  {!preview.uploading && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 absolute -top-2 -right-2 rounded-full bg-destructive text-destructive-foreground opacity-0 group-hover:opacity-100 transition-all shadow-sm hover:bg-destructive hover:text-destructive-foreground"
                      onClick={() => handleRemoveFile(index)}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Message input */}
        <div className="flex items-end gap-2 relative max-w-4xl mx-auto w-full">
          <form
            onSubmit={(e) => handleSendMessage(e)}
            className="flex w-full gap-2 items-end"
          >
            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,application/pdf,audio/*,video/*"
              onChange={handleFileSelect}
              className="hidden"
            />

            <div className="flex gap-1.5 pb-1">
              {/* Attachment button */}
              <motion.button
                type="button"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={handleAttachmentUpload}
                disabled={!isConnected || uploadingFiles}
                className={cn(
                  "w-9 h-9 rounded-full flex items-center justify-center transition-colors",
                  "text-muted-foreground hover:text-primary hover:bg-primary/10",
                  (!isConnected || uploadingFiles) && "opacity-50 cursor-not-allowed"
                )}
                title="Attach file"
              >
                <Paperclip className="h-5 w-5" />
              </motion.button>

              {/* Location sharing button */}
              <motion.button
                type="button"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={handleLocationShare}
                disabled={!isConnected || uploadingFiles || sharingLocation}
                className={cn(
                  "w-9 h-9 rounded-full flex items-center justify-center transition-colors",
                  "text-muted-foreground hover:text-emerald-600 hover:bg-emerald-500/10",
                  (!isConnected || uploadingFiles || sharingLocation) && "opacity-50 cursor-not-allowed"
                )}
                title="Share location"
              >
                {sharingLocation ? (
                  <Loader2 className="h-5 w-5 text-emerald-600 animate-spin" />
                ) : (
                  <MapPin className="h-5 w-5" />
                )}
              </motion.button>
            </div>

            <div className="flex-1 bg-muted/50 rounded-3xl border border-transparent focus-within:border-primary/20 focus-within:bg-background focus-within:shadow-sm transition-all duration-200 flex items-center">
               <Textarea
                ref={inputRef}
                className="border-none shadow-none bg-transparent focus-visible:ring-0 min-h-[44px] max-h-[120px] py-3 px-4 text-base placeholder:text-muted-foreground/70 resize-none"
                value={newMessage}
                onChange={(e) => {
                  setNewMessage(e.target.value);
                  const now = Date.now();
                  if (now - lastTypingRef.current > 1000) {
                    lastTypingRef.current = now;
                    setTyping(true);
                  }
                }}
                onKeyDown={(e) => {
                  // Enter sends the message (unless Shift is held for new line)
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                  // Shift+Enter creates a new line (default behavior)
                }}
                onBlur={() => setTyping(false)}
                placeholder={uploadingFiles ? "Uploading files..." : "Type a message..."}
                disabled={!isConnected || uploadingFiles}
                rows={1}
              />
              
              {/* Voice recorder inside input area */}
              <div className="pr-2">
                 <VoiceRecorder
                  roomName={roomName}
                  entityId={userInfo.id}
                  onSendMessage={sendMessage}
                  onRecordingComplete={(messageId, attachmentData) => {
                    if (messageId && attachmentData) {
                      setPendingAttachments(prev => ({
                        ...prev,
                        [messageId]: [attachmentData]
                      }));
                    }
                    scrollToBottom();
                  }}
                  disabled={!isConnected || uploadingFiles}
                />
              </div>
            </div>

            <Button
              className={cn(
                "rounded-full h-11 w-11 flex-shrink-0 shadow-md transition-all duration-300",
                (!newMessage.trim() && filePreviews.length === 0) 
                  ? "bg-muted text-muted-foreground hover:bg-muted" 
                  : "bg-primary text-primary-foreground hover:bg-primary/90 hover:scale-105"
              )}
              type="submit"
              disabled={!isConnected || (!newMessage.trim() && filePreviews.length === 0) || uploadingFiles}
            >
              <Send className="h-5 w-5 ml-0.5" />
            </Button>
          </form>
        </div>
      </div>

      {/* WhatsApp-style Media Carousel - only show if no external handler */}
      {!externalOnMediaClick && (
        <MediaCarousel
          isOpen={carouselMedia.isOpen}
          onClose={() => setCarouselMedia({ isOpen: false, mediaId: '', messageId: undefined })}
          initialMediaId={carouselMedia.mediaId}
          messageId={carouselMedia.messageId}
          roomName={roomName}
          onViewAllMedia={() => {
            // Re-open carousel without messageId filter
            setCarouselMedia({ isOpen: true, mediaId: carouselMedia.mediaId, messageId: undefined });
          }}
        />
      )}

      {/* Group Info Sheet */}
      {isGroupChat && (
        <GroupInfoSheet
          open={groupInfoOpen}
          onOpenChange={setGroupInfoOpen}
          roomName={roomName}
          currentEntityId={userInfo.id}
          currentEntityRole={userInfo.role}
          onViewProfile={onViewProfile}
          onLeaveGroup={onLeaveGroup}
        />
      )}
    </div>
  );
};
